import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft } from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { AuditTrailViewer } from '../../components/AuditTrail';
import TabNavigation from '../../components/TabNavigation';

const TrainingRecordDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const fetchRecord = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training-records/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecord(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load training record');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Scheduled': return 'bg-gray-100 text-gray-700';
      case 'Failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <Alert variant="destructive">
            <AlertDescription>{error || 'Training record not found'}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/training/records')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Records
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/training/records')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Records
            </Button>

            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{record.employee_name}</h1>
                <p className="text-gray-600 mt-1">{record.course_title}</p>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(record.completion_status)}`}>
                {record.completion_status}
              </span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'audit'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Audit History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' ? (
            <>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Training Record Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">
                        {record.attendee_count > 0 ? 'Attendees' : 'Employee Name'}
                      </label>
                      {/* A group session lists who was there; the header label
                          alone reads as one person's record, which it is not. */}
                      {record.attendee_count > 0 ? (
                        <ul className="mt-1 space-y-1">
                          {(record.attendees || []).map((a, i) => (
                            <li key={a.id || i} className="text-gray-900 flex items-center gap-2">
                              <span>{a.employee_name}</span>
                              {a.contractor_worker_id ? (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">
                                  Contractor{a.contractor_company ? ` · ${a.contractor_company}` : ''}
                                </span>
                              ) : a.employee_master_id ? (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                                  {a.employee_code || 'Employee'}
                                </span>
                              ) : a.is_manual ? (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                                  Not on record
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-900 mt-1">{record.employee_name || '—'}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Course Title</label>
                      <p className="text-gray-900 mt-1">{record.course_title || '—'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Training Date</label>
                      <p className="text-gray-900 mt-1">{formatDate(record.training_date)}</p>
                      {record.training_start && record.training_end && (
                        <p className="text-sm text-gray-500 mt-0.5">
                          {new Date(record.training_start).toLocaleString()} —{' '}
                          {new Date(record.training_end).toLocaleString()}
                          {record.duration_hours ? ` · ${record.duration_hours} h` : ''}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Status</label>
                      <p className="mt-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(record.completion_status)}`}>
                          {record.completion_status}
                        </span>
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Trainer Name</label>
                      <p className="text-gray-900 mt-1">{record.trainer_name || '—'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Duration (hours)</label>
                      <p className="text-gray-900 mt-1">{record.duration_hours || '—'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Quiz Score</label>
                      <p className="text-gray-900 mt-1">{record.quiz_score ? `${record.quiz_score}%` : '—'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Certificate Issued</label>
                      <p className="mt-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          record.certificate_issued ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {record.certificate_issued ? 'Yes' : 'No'}
                        </span>
                      </p>
                    </div>

                    {record.certificate_number && (
                      <div>
                        <label className="text-sm font-semibold text-gray-600">Certificate Number</label>
                        <p className="text-gray-900 mt-1 font-mono">{record.certificate_number}</p>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Attendance Verified</label>
                      <p className="mt-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          record.attendance_verified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {record.attendance_verified ? 'Yes' : 'No'}
                        </span>
                      </p>
                    </div>

                    {record.notes && (
                      <div className="col-span-1 md:col-span-2">
                        <label className="text-sm font-semibold text-gray-600">Notes</label>
                        <p className="text-gray-900 mt-1">{record.notes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Evaluations Section */}
              {(record.trainer_evaluation || record.manager_evaluation) && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>Evaluations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {record.trainer_evaluation && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600">Trainer Evaluation</label>
                          <p className="text-gray-900 mt-1 text-lg font-semibold">{record.trainer_evaluation}/5</p>
                        </div>
                      )}
                      {record.manager_evaluation && (
                        <div>
                          <label className="text-sm font-semibold text-gray-600">Manager Evaluation</label>
                          <p className="text-gray-900 mt-1 text-lg font-semibold">{record.manager_evaluation}/5</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Created</label>
                    <p className="text-gray-900 mt-1">{formatDateTime(record.created_at)}</p>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <AuditTrailViewer
                  entityType="training_record"
                  entityId={id}
                  title="Training Record Audit History"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingRecordDetail;
